import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Refresh from '@/images/Refresh.svg';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { PolotnoStoreActivePageKeys, usePolotnoActivePageState } from '@/pages/AdvancedEditor/hooks';
import { usePresenterDrivers } from '@/pages/AdvancedEditor/hooks/usePresenterDrivers';
import { getAvatarPresenterFromPage } from '@/pages/AdvancedEditor/utils';
import { logger } from '@/services/logger';
import theme from '@/theme';
import { getImageSize } from '@/utils/utils';
import { VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import { ButtonBase } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { RemoveBackground } from './RemoveBackground';
import { VideoContainer } from './VideoContainer';

const ImageContainer = styled.div<{ src: string; isSquare?: boolean }>`
    padding: 12px;
    border-radius: 12px;
    display: flex;
    gap: 12px;
    background-image: url(${({ src }) => src});
    background-size: contain;
    background-position: center;
    background-color: ${({ theme }) => theme.colors.black[100]};
    background-repeat: no-repeat;
    height: 200px;
    width: 100%;
    align-items: end;
    justify-content: end;

    ${({ isSquare }) =>
        isSquare &&
        `
        background-size: contain;
    `}
`;

const ReplaceButton = styled(ButtonBase)`
    display: flex;
    gap: 6px;
    padding: 7px 8px;
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    background-color: white;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.black[200]};

    :hover {
        background-color: ${({ theme }) => theme.colors.black[200]};
    }
`;

interface Props {
    setEditMode: (editMode: boolean) => void;
}

export const AvatarControl = observer(({ setEditMode }: Props) => {
    const { store } = useAdvancedEditorContext();
    const currentPresenter = getAvatarPresenterFromPage(store.activePage);

    const { presenterDrivers } = usePresenterDrivers(currentPresenter);
    const [selectedDriver] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.selectedDriver);

    let currentAvatarWidth = 0;
    let currentAvatarHeight = 0;

    const handleReplace = () => {
        setEditMode(false);
    };

    useEffect(() => {
        if (currentPresenter) {
            getImageSize(currentPresenter.poster).then(({ width, height }) => {
                currentAvatarWidth = width;
                currentAvatarHeight = height;
            });
        }
    }, [currentPresenter]);

    if (!currentPresenter) {
        return null;
    }

    const percentageDiff = (Math.abs(currentAvatarWidth - currentAvatarHeight) / Math.max(currentAvatarWidth, currentAvatarHeight)) * 100;
    const isSquare = percentageDiff < 10; //  10% difference is considered square
    const driver = presenterDrivers.find(driver => driver.driver_id === selectedDriver);
    const showPreviewVideo = driver && currentPresenter?.type === VideoType.CLIP;

    const Container = showPreviewVideo ? VideoContainer : ImageContainer;

    const onMouseEnter = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (!showPreviewVideo) {
            return;
        }

        event.stopPropagation();
        const video = (event as React.MouseEvent<HTMLVideoElement, MouseEvent>).currentTarget;

        if (video.readyState >= 2) {
            video.play().catch(error => logger.error('error playing video', { error }));
            video.addEventListener('ended', () => {
                video.currentTime = 0;
            });
        }
    };

    const src = showPreviewVideo ? driver.preview_url : currentPresenter.poster;

    return (
        <Container src={src} isSquare={isSquare} onMouseEnter={onMouseEnter}>
            <RemoveBackground />

            <Tooltip
                title="Replace Avatar"
                placement="top"
                slotProps={{ tooltip: { sx: { backgroundColor: theme.colors.secondary[900], padding: '8px', fontSize: '14px' } } }}
                PopperProps={{
                    modifiers: [{ name: 'offset', options: { offset: [0, -6] } }],
                }}>
                <ReplaceButton data-testid={`replace-avatar-${currentPresenter.id}`} onClick={handleReplace}>
                    <MaskedIcon src={Refresh} size="18px" />
                    Replace
                </ReplaceButton>
            </Tooltip>
        </Container>
    );
});
